import React from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Typography,
  Box,
} from "@mui/material";
import { Assignment, RateReview } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../StatusChip";

interface SubmissionTask {
  id: string;
  status: string;
  tokenizedLink: string;
  activityName: string;
  activityDueDate?: string | null;
  activityStatus: string;
}

interface ReviewTask {
  id: string;
  reviewLink: string;
  reviewSubmitted: boolean;
  reviewerName: string;
  submissionId: string;
  activityName?: string;
}

interface Props {
  type: "submission" | "review";
  submission?: SubmissionTask;
  review?: ReviewTask;
}

export function SubmissionReviewTaskCard({ type, submission, review }: Props) {
  const [t] = useTranslation();

  if (type === "submission" && submission) {
    const isSubmitted = submission.status === "SB";
    const isPending = submission.activityStatus === "SO" && !isSubmitted;

    return (
      <Card>
        <CardActionArea
          component="a"
          href={submission.tokenizedLink}
          target="_blank"
          rel="noopener noreferrer"
          disabled={isSubmitted}
        >
          <CardHeader
            avatar={<Assignment />}
            title={submission.activityName || String(t("Submission Review"))}
            action={
              <StatusChip
                status={
                  isSubmitted ? "success" : isPending ? "warning" : "error"
                }
                label={
                  isSubmitted
                    ? String(t("Submitted"))
                    : isPending
                      ? String(t("Submit Now"))
                      : String(t("Closed"))
                }
              />
            }
            subheader={
              submission.activityDueDate
                ? `${String(t("Due"))}: ${new Date(
                    submission.activityDueDate,
                  ).toLocaleDateString()}`
                : undefined
            }
          />
          {isPending && (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("Click to submit your work")}
              </Typography>
            </CardContent>
          )}
        </CardActionArea>
      </Card>
    );
  }

  if (type === "review" && review) {
    return (
      <Card>
        <CardActionArea
          component="a"
          href={review.reviewLink}
          target="_blank"
          rel="noopener noreferrer"
          disabled={review.reviewSubmitted}
        >
          <CardHeader
            avatar={<RateReview />}
            title={String(t("Peer Review"))}
            action={
              <StatusChip
                status={review.reviewSubmitted ? "success" : "warning"}
                label={
                  review.reviewSubmitted
                    ? String(t("Completed"))
                    : String(t("Review Now"))
                }
              />
            }
          />
          {!review.reviewSubmitted && (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("Click to complete your peer review")}
              </Typography>
            </CardContent>
          )}
        </CardActionArea>
      </Card>
    );
  }

  return null;
}
